'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { BsFire } from 'react-icons/bs'
import { StarRating } from '@/components/star-rating'

export type CompletionRingData = {
  key: string
  label: string
  shortLabel: string
  rated: number
  total: number
  percent: number
  averageRating: number | null
  color: string
  href?: string
}

type CollectionTopRated = {
  entityId: string
  slug: string
  name: string
  imageUrl: string | null
  type: string
  overallRating: number
  ratedAt: string
}

type CollectionDetailResponse = {
  ring: CompletionRingData
  topRated: CollectionTopRated[]
}

function Ring({
  percent,
  color,
  size = 88,
  stroke = 8,
}: {
  percent: number
  color: string
  size?: number
  stroke?: number
}) {
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (Math.min(100, Math.max(0, percent)) / 100) * circumference

  return (
    <svg width={size} height={size} className="block -rotate-90" aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-[stroke-dashoffset] duration-700 ease-out"
      />
    </svg>
  )
}

function CollectionDetailDialog({
  open,
  collectionKey,
  fallback,
  onClose,
}: {
  open: boolean
  collectionKey: string | null
  fallback: CompletionRingData | null
  onClose: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<CollectionDetailResponse | null>(null)

  useEffect(() => {
    if (!open || !collectionKey) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setDetail(null)

    fetch(`/api/user/collections/${collectionKey}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body.error || 'Failed to load collection')
        }
        return res.json() as Promise<CollectionDetailResponse>
      })
      .then((data) => {
        if (!cancelled) setDetail(data)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Failed to load collection')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, collectionKey])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !fallback) return null

  const ring = detail?.ring ?? fallback
  const topRated = detail?.topRated ?? []

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-detail-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h2 id="collection-detail-title" className="text-xl font-bold text-slate-800">
              {ring.label}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {ring.rated} of {ring.total} rated · {ring.percent}% complete
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-4 mb-6 p-4 rounded-lg bg-slate-50 border border-slate-100">
            <div className="relative flex-shrink-0">
              <Ring percent={ring.percent} color={ring.color} size={72} stroke={7} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-slate-800">{ring.percent}%</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Your overall score</p>
              {ring.averageRating != null ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-3xl font-bold text-slate-800">
                    {ring.averageRating.toFixed(1)}
                  </span>
                  <StarRating
                    rating={ring.averageRating}
                    size="sm"
                    variant="user"
                    orientation="horizontal"
                  />
                </div>
              ) : (
                <p className="text-base text-slate-500 mt-1">No ratings yet</p>
              )}
            </div>
          </div>

          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Your top 10
          </h3>

          {loading && (
            <p className="text-sm text-slate-500 py-6 text-center">Loading your ratings…</p>
          )}
          {error && !loading && (
            <p className="text-sm text-red-600 py-6 text-center">{error}</p>
          )}
          {!loading && !error && topRated.length === 0 && (
            <p className="text-sm text-slate-500 py-6 text-center">
              You haven&apos;t rated anything in this collection yet.
            </p>
          )}
          {!loading && !error && topRated.length > 0 && (
            <ol className="space-y-2">
              {topRated.map((item, index) => (
                <li key={item.entityId}>
                  <Link
                    href={`/rate/${item.slug}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                    onClick={onClose}
                  >
                    <span
                      className="w-6 text-center text-sm font-bold flex-shrink-0"
                      style={{ color: ring.color }}
                    >
                      {index + 1}
                    </span>
                    {item.imageUrl ? (
                      <div className="relative w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-slate-100">
                        <Image
                          src={item.imageUrl}
                          alt=""
                          fill
                          className="object-cover"
                          sizes="40px"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-md bg-slate-100 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{item.name}</p>
                      <p className="text-xs text-slate-500 capitalize">
                        {item.type.toLowerCase()}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <StarRating
                        rating={item.overallRating}
                        size="sm"
                        variant="user"
                        orientation="horizontal"
                        showNumber
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>

        {ring.href && (
          <div className="px-6 py-4 border-t border-slate-200">
            <Link
              href={ring.href}
              onClick={onClose}
              className="block w-full text-center text-sm font-semibold text-[#1E4289] hover:underline"
            >
              Browse {ring.label}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export function CompletionRings({ rings }: { rings: CompletionRingData[] }) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  if (rings.length === 0) return null

  const selected = rings.find((r) => r.key === selectedKey) ?? null

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm mb-8">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-800">Collection Progress</h2>
          <p className="text-sm text-slate-500 mt-1">
            Distinct experiences you&apos;ve rated across WDW — tap a collection for details
          </p>
        </div>
        <div className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {rings.map((ring) => (
            <button
              key={ring.key}
              type="button"
              onClick={() => setSelectedKey(ring.key)}
              className="flex flex-col items-center text-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4289] focus-visible:ring-offset-2 rounded-lg"
            >
              <div className="relative mb-2">
                <Ring percent={ring.percent} color={ring.color} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-slate-800 leading-none">
                    {ring.percent}%
                  </span>
                  <span className="text-[10px] text-slate-500 mt-0.5">
                    {ring.rated}/{ring.total}
                  </span>
                </div>
              </div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-[#1E4289] transition-colors">
                {ring.label}
              </p>
              {ring.averageRating != null ? (
                <div className="mt-1.5 flex justify-center">
                  <StarRating
                    rating={ring.averageRating}
                    size="sm"
                    variant="user"
                    orientation="horizontal"
                    showNumber
                  />
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-0.5">No ratings yet</p>
              )}
            </button>
          ))}
        </div>
      </div>

      <CollectionDetailDialog
        open={selectedKey != null}
        collectionKey={selectedKey}
        fallback={selected}
        onClose={() => setSelectedKey(null)}
      />
    </>
  )
}

export function StreakBadge({
  currentStreak,
  longestStreak,
  activeToday,
  compact = false,
}: {
  currentStreak: number
  longestStreak?: number
  activeToday?: boolean
  compact?: boolean
}) {
  if (compact) {
    return (
      <div
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold flex-shrink-0 ${
          currentStreak > 0
            ? 'bg-[#FEDB1A] text-[#1E4289]'
            : 'bg-white/15 text-white/80'
        }`}
        title={
          activeToday
            ? `${currentStreak}-day streak — you're active today`
            : currentStreak > 0
              ? `${currentStreak}-day streak — rate or vote today to keep it`
              : 'Rate or vote today to start a streak'
        }
      >
        <BsFire className="text-base" />
        <span>{currentStreak}</span>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">Daily Streak</p>
          <p className="text-3xl font-bold text-slate-800 mt-1 flex items-center gap-2">
            <BsFire className="text-orange-500" />
            {currentStreak}
            <span className="text-base font-medium text-slate-500">
              {currentStreak === 1 ? 'day' : 'days'}
            </span>
          </p>
          {typeof longestStreak === 'number' && (
            <p className="text-xs text-slate-500 mt-1">Best: {longestStreak} days</p>
          )}
          {!activeToday && currentStreak > 0 && (
            <p className="text-xs text-amber-600 mt-1">Rate or vote today to keep it going</p>
          )}
          {activeToday && (
            <p className="text-xs text-green-600 mt-1">Active today — streak safe</p>
          )}
        </div>
        <div className="bg-orange-100 p-3 rounded-lg">
          <BsFire className="w-8 h-8 text-orange-500" />
        </div>
      </div>
    </div>
  )
}
